/**
 * WeeklyPlanningView - Spreadsheet-style grid for weekly production planning.
 * Manager enters Product SKU, Date, Cases — system auto-resolves materials
 * from ics.production_inputs and calculates est. production lbs.
 * Sessions created as plan-only with material_fulfillment_status='pending'.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  Plus, Trash2, Save, CheckCircle,
  Package, Loader2, Calendar
} from 'lucide-react';
import DatePickerDropdown from '../../../components/DatePickerDropdown';
import productionService from '../../../services/productionService';
import { DEFAULT_YIELD } from '../../../hooks/useYieldFactor';
import { useLanguage } from '../../../i18n/LanguageContext';

interface PlanRow {
  id: string; // temp client-side ID
  scheduled_date: string;
  product_sku: string;
  product_name: string;
  packing: string;
  unit_weight: number;
  cases: number;
  est_production_lbs: number;
  material_item_number: string;
  material_name: string;
  brand: string;
  location: string;
  yield_factor: number;
  saved: boolean; // whether this row has been saved to the backend
  session_number?: string; // set after saving
  fulfillment_status?: string;
}

interface WeeklyPlanningViewProps {
  weekDates: string[];
  selectedDate: string;
}

const WeeklyPlanningView: React.FC<WeeklyPlanningViewProps> = ({ weekDates, selectedDate }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const [planRows, setPlanRows] = useState<PlanRow[]>([]);
  const [savingRowId, setSavingRowId] = useState<string | null>(null);

  // Fetch products for dropdown
  const { data: products = [] } = useQuery(
    ['products'],
    () => productionService.getProducts(),
    { staleTime: 60000 }
  );

  // Fetch existing plan-only sessions for the week
  const dateFrom = weekDates[0];
  const dateTo = weekDates[6];

  const { data: existingSessions = [] } = useQuery(
    ['productionSessions'],
    () => productionService.getProductionSessions(),
    { staleTime: 20000 }
  );

  // Filter to plan-only sessions in this week
  const planOnlySessions = useMemo(() => {
    return existingSessions.filter((s: any) => {
      const sessionDate = s.scheduled_date?.split('T')[0];
      return sessionDate >= dateFrom && sessionDate <= dateTo &&
             s.material_fulfillment_status && s.material_fulfillment_status !== 'not_required';
    });
  }, [existingSessions, dateFrom, dateTo]);

  // Create plan-only session mutation
  const createPlanMutation = useMutation(
    (row: PlanRow) => productionService.createProductionSession({
      product_sku: row.product_sku,
      target_output_weight: row.est_production_lbs,
      total_boxes: row.cases,
      scheduled_date: row.scheduled_date,
      plan_only: true,
    }),
    {
      onSuccess: (data, row) => {
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['stockCheck']);
        setPlanRows(prev => prev.map(r =>
          r.id === row.id ? { ...r, saved: true, session_number: data.session?.session_number } : r
        ));
        toast.success(t('planning.sessionCreated'));
        setSavingRowId(null);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || t('planning.sessionsFailed'));
        setSavingRowId(null);
      },
    }
  );

  // Handle product selection — auto-fill product name, packing, material info
  const handleProductChange = useCallback(async (rowId: string, productSku: string) => {
    const product = (products as any[]).find((p: any) => p.item_number === productSku);
    if (!product) return;

    // Fetch raw materials for this product
    try {
      const { raw_materials: materials } = await productionService.getRawMaterials(productSku);
      const primaryMaterial = materials?.[0]; // Use first/primary material

      setPlanRows(prev => prev.map(r => {
        if (r.id !== rowId) return r;
        const yieldFactor = primaryMaterial?.yield_factor || product.yield_factor || DEFAULT_YIELD;
        const estLbs = r.cases > 0 ? r.cases * (product.unit_weight || 0) : 0;
        return {
          ...r,
          product_sku: productSku,
          product_name: product.item_english_name || '',
          packing: product.packing || '',
          unit_weight: product.unit_weight || 0,
          est_production_lbs: estLbs,
          material_item_number: primaryMaterial?.item_number || '',
          material_name: primaryMaterial?.item_english_name || '',
          brand: primaryMaterial?.english_brand_name || '',
          location: primaryMaterial?.location_code || primaryMaterial?.lot_location_code || '',
          yield_factor: yieldFactor,
        };
      }));
    } catch (err) {
      // If raw materials fetch fails, still update product info
      setPlanRows(prev => prev.map(r => {
        if (r.id !== rowId) return r;
        return {
          ...r,
          product_sku: productSku,
          product_name: product.item_english_name || '',
          packing: product.packing || '',
          unit_weight: product.unit_weight || 0,
        };
      }));
    }
  }, [products]);

  // Handle cases change — auto-calc est production lbs
  const handleCasesChange = useCallback((rowId: string, cases: number) => {
    setPlanRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      return {
        ...r,
        cases,
        est_production_lbs: cases * r.unit_weight,
      };
    }));
  }, []);

  // Add new empty row
  const addRow = () => {
    setPlanRows(prev => [...prev, {
      id: `new-${Date.now()}`,
      scheduled_date: selectedDate,
      product_sku: '',
      product_name: '',
      packing: '',
      unit_weight: 0,
      cases: 0,
      est_production_lbs: 0,
      material_item_number: '',
      material_name: '',
      brand: '',
      location: '',
      yield_factor: 0,
      saved: false,
    }]);
  };

  // Remove unsaved row
  const removeRow = (rowId: string) => {
    setPlanRows(prev => prev.filter(r => r.id !== rowId));
  };

  // Save a single row (create plan-only session)
  const saveRow = (row: PlanRow) => {
    if (!row.product_sku || row.cases <= 0) {
      toast.error('Product and cases are required');
      return;
    }
    setSavingRowId(row.id);
    createPlanMutation.mutate(row);
  };

  // Get fulfillment status color
  const getFulfillmentColor = (status?: string) => {
    switch (status) {
      case 'fulfilled': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'partial': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'pending': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'unfulfilled': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getFulfillmentLabel = (status?: string) => {
    switch (status) {
      case 'fulfilled': return t('planning.fulfilled');
      case 'partial': return t('planning.partial');
      case 'pending': return t('planning.pending');
      case 'unfulfilled': return t('planning.unfulfilled');
      case 'not_required': return t('planning.notRequired');
      default: return '-';
    }
  };

  return (
    <div className="space-y-4">
      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Package className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          {t('planning.title')}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={addRow}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('planning.addRow')}</span>
          </button>
        </div>
      </div>

      {/* Existing Plan-Only Sessions (from DB) */}
      {planOnlySessions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-100 dark:border-indigo-800">
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
              {t('planning.planOnlySession')} ({planOnlySessions.length})
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.date')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.productSku')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.productName')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.cases')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.estProductionLbs')}</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.fulfillment')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {planOnlySessions.map((session: any) => (
                  <tr key={session.session_number} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-2">{session.scheduled_date?.split('T')[0]}</td>
                    <td className="px-3 py-2 font-mono text-xs">{session.product_sku}</td>
                    <td className="px-3 py-2">{session.product_name}</td>
                    <td className="px-3 py-2 text-right">{session.total_boxes || session.cases || 0}</td>
                    <td className="px-3 py-2 text-right">{parseFloat(session.target_output_weight || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getFulfillmentColor(session.material_fulfillment_status)}`}>
                        {getFulfillmentLabel(session.material_fulfillment_status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New Plan Rows (unsaved) */}
      {planRows.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20 border-b border-green-100 dark:border-green-800">
            <span className="text-sm font-medium text-green-700 dark:text-green-300">
              New Plan Rows ({planRows.filter(r => !r.saved).length} unsaved)
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.date')}</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.productSku')}</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.productName')}</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.packing')}</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.cases')}</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.estProductionLbs')}</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.material')}</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.materialName')}</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('planning.supplierBrand')}</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {planRows.map((row) => (
                  <tr key={row.id} className={row.saved ? 'bg-green-50 dark:bg-green-900/10' : ''}>
                    {/* Date */}
                    <td className="px-2 py-2">
                      {row.saved ? (
                        <span className="w-32 inline-block px-2 py-1 text-sm text-gray-900 dark:text-white opacity-50">
                          {row.scheduled_date ? (() => { const d = new Date(row.scheduled_date + 'T00:00:00'); return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); })() : '—'}
                        </span>
                      ) : (
                        <DatePickerDropdown
                          selected={row.scheduled_date || null}
                          onSelect={(date) => setPlanRows(prev => prev.map(r => r.id === row.id ? { ...r, scheduled_date: date } : r))}
                        >
                          {({ onClick }) => (
                            <button type="button" onClick={onClick} className="w-32 flex items-center justify-between px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                              {row.scheduled_date ? (() => { const d = new Date(row.scheduled_date + 'T00:00:00'); return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); })() : 'Select...'}
                              <Calendar className="w-3 h-3 text-gray-400 shrink-0" />
                            </button>
                          )}
                        </DatePickerDropdown>
                      )}
                    </td>
                    {/* Product SKU dropdown */}
                    <td className="px-2 py-2">
                      <select
                        value={row.product_sku}
                        onChange={(e) => handleProductChange(row.id, e.target.value)}
                        disabled={row.saved}
                        className="w-36 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                      >
                        <option value="">{t('planning.selectProduct')}</option>
                        {(products as any[]).map((p: any) => (
                          <option key={p.item_number} value={p.item_number}>
                            {p.item_number}
                          </option>
                        ))}
                      </select>
                    </td>
                    {/* Product Name (auto-fill) */}
                    <td className="px-2 py-2 text-gray-600 dark:text-gray-400 truncate max-w-[150px]" title={row.product_name}>
                      {row.product_name || '-'}
                    </td>
                    {/* Packing (auto-fill) */}
                    <td className="px-2 py-2 text-gray-600 dark:text-gray-400">
                      {row.packing || '-'}
                    </td>
                    {/* Cases (editable) */}
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        value={row.cases || ''}
                        onChange={(e) => handleCasesChange(row.id, parseInt(e.target.value) || 0)}
                        disabled={row.saved}
                        min="0"
                        className="w-20 px-2 py-1 text-sm text-right border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                      />
                    </td>
                    {/* Est Production lbs (auto-calc) */}
                    <td className="px-2 py-2 text-right font-medium">
                      {row.est_production_lbs > 0 ? row.est_production_lbs.toFixed(2) : '-'}
                    </td>
                    {/* Material (auto-resolved) */}
                    <td className="px-2 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                      {row.material_item_number || '-'}
                    </td>
                    {/* Material Name */}
                    <td className="px-2 py-2 text-gray-600 dark:text-gray-400 truncate max-w-[120px]" title={row.material_name}>
                      {row.material_name || '-'}
                    </td>
                    {/* Brand */}
                    <td className="px-2 py-2 text-gray-600 dark:text-gray-400">
                      {row.brand || '-'}
                    </td>
                    {/* Actions */}
                    <td className="px-2 py-2 text-center">
                      {row.saved ? (
                        <CheckCircle className="w-4 h-4 text-green-500 inline" />
                      ) : (
                        <div className="flex items-center gap-1 justify-center">
                          <button
                            onClick={() => saveRow(row)}
                            disabled={savingRowId === row.id || !row.product_sku || row.cases <= 0}
                            className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded disabled:opacity-30"
                            title={t('planning.savePlan')}
                          >
                            {savingRowId === row.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => removeRow(row.id)}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            title={t('planning.removeRow')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {planRows.length === 0 && planOnlySessions.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <Package className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400 mb-4">{t('planning.noProductSelected')}</p>
          <button
            onClick={addRow}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('planning.addRow')}
          </button>
        </div>
      )}
    </div>
  );
};

export default WeeklyPlanningView;
